import { LoadingOutlined } from '@ant-design/icons';
import { Card, Divider, Result, Spin } from 'antd';
import { observer } from 'mobx-react-lite';
import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { EmptyState } from './components/EmptyState';
import { FailedState } from './components/FailedState';
import { HistorySidebar } from './components/HistorySidebar';
import { PairingForm } from './components/PairingForm';
import { PairingView } from './components/PairingView';
import { PairingProvider, usePairingStore } from './store/PairingContext';

const PairingPageContent = observer(() => {
  const location = useLocation();
  const pairingStore = usePairingStore();

  useEffect(() => {
    const state = location.state as { menu?: any } | null;
    if (state?.menu) {
      pairingStore.setCurrentSelection({
        type: 'menu',
        menu: state.menu,
      });
      // Clear the state to prevent reselection on page refresh
      window.history.replaceState({}, document.title);
    }
  }, [location.state, pairingStore]);

  useEffect(() => {
    pairingStore.fetchPairingData();
  }, [pairingStore]);

  const renderStatusContent = (status: string) => {
    switch (status) {
      case 'pending':
        return (
          <Result
            icon={
              <Spin
                indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />}
              />
            }
            title="Pending"
            subTitle="Waiting for processing..."
          />
        );
      case 'processing':
        return (
          <Result
            icon={
              <Spin
                indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />}
              />
            }
            title="Processing"
            subTitle="Generating pairing suggestions..."
          />
        );
      case 'failed':
        return <FailedState />;
      case 'completed':
        return <PairingView />;
      default:
        return <EmptyState />;
    }
  };

  const renderContent = () => {
    if (pairingStore.isPairingFormVisible) {
      return (
        <PairingForm
          item={pairingStore.currentSelection}
          onSuccess={pairingStore.onSuccess}
          onClose={() => pairingStore.setPairingFormVisible(false)}
        />
      );
    }

    if (!pairingStore.currentSelection) {
      return <EmptyState />;
    }

    if (pairingStore.currentSelection.type === 'menu') {
      if (!pairingStore.currentSelection.menu.pairing) {
        return (
          <PairingForm
            item={pairingStore.currentSelection}
            onSuccess={pairingStore.onSuccess}
          />
        );
      }
      return renderStatusContent(
        pairingStore.currentSelection.menu.pairing.status,
      );
    }

    return renderStatusContent(pairingStore.currentSelection.pairing.status);
  };

  return (
    <div className="flex p-8 gap-4 h-full">
      <Card
        className="h-full overflow-hidden"
        styles={{
          body: {
            height: '100%',
            overflow: 'hidden',
            padding: 0,
          },
        }}
      >
        <HistorySidebar />
      </Card>

      <div className="mx-4 flex items-stretch">
        <Divider type="vertical" style={{ height: '100%' }} />
      </div>
      <Card
        className="flex-1 h-full overflow-y-auto"
        styles={{
          body: {
            height: '100%',
          },
        }}
      >
        {renderContent()}
      </Card>
    </div>
  );
});

export function PairingPage() {
  return (
    <PairingProvider>
      <PairingPageContent />
    </PairingProvider>
  );
}
